import {
  FILE_CHIP_RE,
  stripMentionSyntax,
} from '@/components/tiptap/mention-types';
import type { OrgChatAttachmentInput } from '@/components/org-chat/org-chat-types';
import type { OrgThreadDraftAttachment } from '@/convex/organizations/threads/draft_validators';

/**
 * Text normalization helpers for the composer's serialized editor content,
 * plus the shared-draft attachment projection.
 */

export function stripDanglingMentionTriggers(text: string): string {
  return text
    .replace(/(^|\s)@+(?=\s|$)/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ *\n */g, '\n')
    .trim();
}

export function getVisiblePromptText(text: string): string {
  return stripDanglingMentionTriggers(
    stripMentionSyntax(text.replace(FILE_CHIP_RE, ''))
  );
}

export function toAssistantMetadataInstruction(text: string): string {
  return `metadata: ${JSON.stringify({ message_to_assistant: text.trim() })}`;
}

export function ensureAssistantMetadataInstruction(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';

  const nonEmptyLines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (nonEmptyLines.every((line) => line.startsWith('metadata:'))) {
    return trimmed;
  }

  return toAssistantMetadataInstruction(trimmed);
}

/**
 * The draft attachment shape is owned by the Convex validator in
 * `convex/organizations/threads/draft_validators.ts` (used by the
 * `orgThreadDrafts` schema and the `updateSharedDraft` args). Deriving the
 * type from it means adding a field to this projection without first adding
 * it to the validator is a compile-time error instead of a runtime schema
 * rejection in production.
 */
export type SharedDraftAttachment = OrgThreadDraftAttachment;

export function toSharedDraftAttachment(
  upload: OrgChatAttachmentInput
): SharedDraftAttachment {
  return {
    kind: upload.kind,
    filename: upload.filename,
    mimeType: upload.mimeType,
    sizeBytes: upload.sizeBytes,
    ...(upload.uploadId ? { uploadId: upload.uploadId } : {}),
    ...(upload.storageId ? { storageId: upload.storageId } : {}),
    ...(upload.storageKey ? { storageKey: upload.storageKey } : {}),
    ...(upload.url ? { url: upload.url } : {}),
    ...(upload.workspaceFileId
      ? { workspaceFileId: upload.workspaceFileId }
      : {}),
    ...(upload.vfsPath ? { vfsPath: upload.vfsPath } : {}),
    ...(upload.source ? { source: upload.source } : {}),
    ...(upload.referenceCategory
      ? { referenceCategory: upload.referenceCategory }
      : {}),
  };
}
